import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { EASE } from "@/components/Reveal";

export const Monogram = ({ compact = false }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <img
      src="/images/logo-icon-bronze.png"
      alt="Sai Leela Decor logo"
      className="h-11 w-auto transition-transform duration-500 group-hover:scale-105"
    />
    {!compact && (
      <span className="hidden sm:block text-[10px] uppercase tracking-[0.35em] text-bone/80 leading-relaxed">
        Sai Leela
        <br />
        <span className="text-bronze">Decor</span>
        <span className="block mt-1.5 h-px w-full bg-bronze/70" />
        <span data-testid="nav-firm-tagline" className="block mt-1.5 text-[9px] tracking-[0.18em] text-bronze/90 whitespace-nowrap">A Rajmata Jijau Enterprises Firm</span>
      </span>
    )}
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
          scrolled ? "backdrop-blur-xl bg-obsidian/70 border-b border-white/[0.06]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Monogram />
          <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `link-underline text-[11px] uppercase tracking-[0.25em] transition-colors duration-500 ${
                    isActive ? "active text-bronze" : "text-bone/70 hover:text-bone"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="nav-consult-btn"
              className="hidden lg:inline-flex items-center gap-2 border border-bronze/40 text-bronze text-[11px] uppercase tracking-[0.25em] px-5 py-2.5 transition-all duration-500 hover:bg-bronze hover:text-obsidian"
            >
              Get a Quote <ArrowUpRight size={13} />
            </Link>
            <button
              data-testid="menu-toggle-btn"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-bone p-2"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-obsidian/95 backdrop-blur-2xl flex flex-col justify-center px-8"
          >
            {NAV_LINKS.map(({ to, label }, i) => (
              <motion.div
                key={to}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.6, ease: EASE }}
              >
                <NavLink
                  to={to}
                  data-testid={`mobile-nav-${label.toLowerCase()}-link`}
                  className={({ isActive }) =>
                    `block font-serif text-4xl font-light py-3 ${
                      isActive ? "text-bronze italic" : "text-bone"
                    }`
                  }
                >
                  {label}
                </NavLink>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
